import { useEffect, useState } from 'react';
import { Box, Typography } from '@mui/material';
import { useParams } from 'react-router-dom';
import { makeStyles } from 'tss-react/mui';


import { getFirmTransactions } from '../../../api/bankingApi/bankingApi';
import TableContainer from '../../../components/Table/TableContainer';
import TableFilters from '../../../components/TableFilters/TableFilters';
import TableActions from '../../../components/Table/TableActions';
import Loader from '../../../components/Loader';
import NoDataText from '../../../components/NoDataText';
import { FIRM_TRANSACTION_LIST_TABLE_LABELS } from '../../../constants/tableLabels';
import { handleSortTableData } from '../../../helpers/handleSortTableData';
import usePagination from '../../../hooks/usePagination';
import useSnackBar from '../../../hooks/useSnackBar';
import {
  ActiveSortField,
  FirmTransaction,
  FirmTransactionsResultSet,
  FilterTypes,
} from '../../../types';

import FirmTransactionsTableDataRow from './FirmTransactionsTableDataRow';
import { downloadCsvorExcel } from '../../../helpers/downloadCsv';
import {
  ACCOUNT_TYPE_FILTER_OPTIONS,
  TRANSACTION_TYPE_FILTER_OPTIONS,
} from '../../../components/TableFilters/constants';
import { numberToUSDCurrency } from '../../../helpers/numberToUSDCurrency';

const useStyles = makeStyles()(() => ({
  systemAccountsSmallLabel: {
    fontSize: '12px',
    fontWeight: 500,
    lineHeight: '120%',
    color: '#93959E',
    letterSpacing: '0.2px',
    fontFamily: 'Inter',
    marginBottom: '4px',
  },
  systemAccountsProjectedBalance: {
    fontSize: '22px',
    fontWeight: 500,
    lineHeight: '120%',
    color: '#93959E',
    letterSpacing: '0.2px',
    fontFamily: 'Inter',
    marginBottom: '4px',
  },
  hiddenBalancePlaceholder: {
    visibility: 'hidden',
  },
  systemAccountsBalanceText: {
    fontSize: '34px',
    fontWeight: 600,
    lineHeight: '120%',
    fontFamily: 'Poppins',
    color: '#FFF',
    marginTop: '4px',
    marginBottom: '8px',
  },
}));

const formatFetchedOn = (date: Date) => {
  const formattedDate = date.toLocaleDateString('en-GB', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
  });

  const formattedTime = date.toLocaleTimeString('en-US', {
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
  });

  return `${formattedDate}, ${formattedTime}`;
};

const accountTypeToKey: Record<string, keyof FirmTransactionsResultSet['accounts']> = {
  REV: 'rev',
  EXP: 'exp',
  CASE: 'case',
  'Revenue Recognition': 'rev',
  'Expense Reimbursement': 'exp',
  'Case Account': 'case',
};

const FirmBankingTransactionsView = () => {
  const params = useParams();
  const { classes } = useStyles();
  const { pageNumber, rowsPerPage, setTotalCount } = usePagination();
  const { setAlert } = useSnackBar();

  const [transactions, setTransactions] = useState<FirmTransaction[]>([]);
  const [isDataLoading, setIsDataLoading] = useState<boolean>(true);
  const [filters, setFilters] = useState<FilterTypes>({
    from: '',
    to: '',
    accountType: 'REV',
  });
  const [showFilters, setShowFilters] = useState<boolean>(true);
  const [activeSortField, setActiveSortField] = useState<ActiveSortField | null>(null);
  const [match, setMatch] = useState<string>('');
  const [fetchedOn, setFetchedOn] = useState<string>('-');
  const [accounts, setAccounts] = useState<FirmTransactionsResultSet['accounts'] | null>(null);

  const sortableFields = {
    lastUpdatedOn: {
      order: '',
      fieldName: 'lastUpdatedOn',
    },
  };

  const hasParams = !!Object.entries(params).length;

  useEffect(() => {
    getFirmTransactions({
      filters: { ...filters, ...(hasParams && params) },
      match: match,
      activeSortField,
    })
      .then((res) => {
        setFetchedOn(formatFetchedOn(new Date()));

        if (res.data.resultSet) {
          setTotalCount?.(res.data.resultSet.length || 0);
          setTransactions(res.data.resultSet);
          setAccounts(res.data.accounts);
          setIsDataLoading(false);
        }
      })
      .catch((error) => {
        setAlert((prev) => ({
          ...prev,
          message: error.response?.data.message || 'Error. Something went wrong...',
          type: 'error',
        }));
      })
  }, [filters, match, activeSortField]);

  const exportList = () => {
    downloadCsvorExcel('v1/fo/banking/transactions/export', 'POST', 'transactions_List','csv', {
      page: pageNumber,
      size: rowsPerPage,
      count: true,
      match: match,
      filters: { ...filters, ...(hasParams && params) },
    }).then((res) => {
      if (res?.data) {
        setAlert({
          message: 'transactions exported successfully.',
          type: 'success',
        });
      }
    });
  };

  // Calculate paginated data
  const paginatedTransactions = transactions.slice(
    (pageNumber - 1) * rowsPerPage,
    pageNumber * rowsPerPage
  );

  const selectedAccountKey = filters.accountType
    ? accountTypeToKey[filters.accountType]
    : undefined;
  const accountSummaries = accounts
    ? selectedAccountKey
      ? [accounts[selectedAccountKey]]
      : Object.values(accounts)
    : [];
  const ledgerBalance = accountSummaries.length
    ? accountSummaries.reduce((total, account) => total + Number(account.balance), 0)
    : null;
  const projectedBalance = accountSummaries.length
    ? accountSummaries.reduce((total, account) => total + Number(account.projectedBalance), 0)
    : null;

  const shouldShowProjectedBalance =
    ledgerBalance === null || projectedBalance === null
      ? ledgerBalance !== projectedBalance
      : Math.abs(projectedBalance - ledgerBalance) >= 0.005;

  return (
    <>
      {isDataLoading && !transactions.length ? (
        <Box position="absolute" top="50%" left="50%">
          <Loader colorType="warning" />
        </Box>
      ) : (
        <>
          <TableActions
            showFilters={showFilters}
            setShowFilters={setShowFilters}
            match={match}
            setMatch={setMatch}
            exportList={exportList}
          />
          <Box display="flex" alignItems="center" marginBottom="32px" gap="40px">
            <Box>
              <Typography className={classes.systemAccountsSmallLabel}>
                Ledger Balance
              </Typography>
              <Typography className={classes.systemAccountsBalanceText}>
                {ledgerBalance === null ? '-' : numberToUSDCurrency(ledgerBalance)}
              </Typography>
              <Typography className={classes.systemAccountsSmallLabel}>
                {`As of ${fetchedOn}`}
              </Typography>
            </Box>
            <Box
              aria-hidden={!shouldShowProjectedBalance}
              className={!shouldShowProjectedBalance ? classes.hiddenBalancePlaceholder : undefined}
            >
              <Typography className={classes.systemAccountsSmallLabel}>
                Projected Balance
              </Typography>
              <Typography className={classes.systemAccountsProjectedBalance}>
                {
                  projectedBalance === null ? '-' : numberToUSDCurrency(projectedBalance)
                }
              </Typography>
              <Typography className={classes.systemAccountsSmallLabel}>
                Reflects queued transactions
              </Typography>
              <Typography className={classes.systemAccountsSmallLabel}>
                {`As of ${fetchedOn}`}
              </Typography>
            </Box>
          </Box>

          {showFilters && (
            <TableFilters
              filters={filters}
              setFilters={setFilters}
              setMatch={setMatch}
              dateRange
              accountType={ACCOUNT_TYPE_FILTER_OPTIONS}
              transactionType={TRANSACTION_TYPE_FILTER_OPTIONS}
              bankingTransactionDefaultFilter={{ accountType: 'REV' }}
            />
          )}

          {!isDataLoading && transactions.length === 0 ? (
            <NoDataText />
          ) : (
            <TableContainer
              headerLabels={FIRM_TRANSACTION_LIST_TABLE_LABELS}
              sortableFields={sortableFields}
              activeSortField={activeSortField}
              setActiveSortField={setActiveSortField}
              handleSortTableData={handleSortTableData}
            >
              <FirmTransactionsTableDataRow tableData={paginatedTransactions} />
            </TableContainer>
          )}
        </>
      )}
    </>
  )
}

export default FirmBankingTransactionsView;
